import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Box, FormControlLabel, Grid, Switch, TextField, Typography, IconButton, Button } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';

import TimeAutocomplete from '../TimeAutocomplete';

import RequestPermissionTooltip from '@/components/RequestPermissionTooltip';
import { useAccess } from '@/Contexts/Permissions';
import { ProjectContext } from '@/Contexts/Project/Context';
import { PermissionActionEnum, PermissionEntityEnum } from '@/graphql/generated/graphql';
import { objectLength } from '@/utils/helperFunctions';

export interface ShiftType {
  name: string;
  startTime: string;
  endTime: string;
}

interface ProjectShiftsProps {
  settingName: string;
  settingValue: string;
  handleUpdate: (settingValue: string) => void;
  editMode?: boolean;
  defaultChecked?: boolean;
  canEditShifts?: boolean;
  projectId?: string;
}

const parseShifts = (value: string): ShiftType[] => {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const ProjectShifts: React.FC<ProjectShiftsProps> = ({
  settingName,
  settingValue,
  handleUpdate,
  editMode,
  defaultChecked = false,
  canEditShifts = true,
  projectId,
}) => {
  const { checkRequestExists, requestPermission } = useAccess();
  const { project } = useContext(ProjectContext);
  const targetProjectId = projectId || project?.id || undefined;

  const handleRequestPermission = (action: PermissionActionEnum, entity: PermissionEntityEnum) => {
    requestPermission(action, entity, targetProjectId);
  };

  const [projectShifts, setProjectShifts] = useState<ShiftType[]>(() => parseShifts(settingValue));
  const [enableProjectShifts, setEnableProjectShifts] = useState<boolean>(defaultChecked);

  // for keeping local UI state aligned with parent value (since otherwise it goes out of sync
  // when the query responds a little late)
  useEffect(() => {
    setEnableProjectShifts(defaultChecked);
  }, [defaultChecked]);

  // for keeping local UI state aligned with parent value (since otherwise it goes out of sync
  // when the query responds a little late)
  useEffect(() => {
    if (settingValue) setProjectShifts(parseShifts(settingValue));
  }, [settingValue]);

  const handleDefaultShifts = (checked: boolean) => {
    setEnableProjectShifts(checked);
    const newValue = checked ? JSON.stringify(projectShifts) : '';
    handleUpdate(newValue);
  };

  const handleShiftChange = (index: number, field: keyof ShiftType, value: string) => {
    const updatedShifts = [...projectShifts];
    updatedShifts[index] = { ...updatedShifts[index], [field]: value };

    setProjectShifts(updatedShifts);
    handleUpdate(JSON.stringify(updatedShifts));
  };

  const handleTimeChange = (index: number, field: 'startTime' | 'endTime', value: string | null) => {
    const timeString = value || '';

    const updatedShifts = [...projectShifts];
    updatedShifts[index] = { ...updatedShifts[index], [field]: timeString };

    setProjectShifts(updatedShifts);
    handleUpdate(JSON.stringify(updatedShifts));
  };

  const handleAddShift = () => {
    const newShift: ShiftType = {
      name: '',
      startTime: '09:00',
      endTime: '17:00',
    };
    setProjectShifts([...projectShifts, newShift]);
    handleUpdate(JSON.stringify([...projectShifts, newShift]));
  };

  const handleDeleteShift = (index: number) => {
    const updatedShifts = projectShifts.filter((_, i) => i !== index);
    setProjectShifts(updatedShifts);
    handleUpdate(JSON.stringify(updatedShifts));
  };

  return (
    <Grid container spacing={2}>
      <Grid display="flex" alignItems="center" size={12}>
        <RequestPermissionTooltip
          hasPermission={canEditShifts}
          requiredPermission={{
            action: PermissionActionEnum.Approve,
            entity: PermissionEntityEnum.CustomShifts,
          }}
          checkRequestExists={checkRequestExists}
          onRequest={handleRequestPermission}
          title={enableProjectShifts ? 'Disable Project Shifts' : 'Enable Project Shifts'}
        >
          <FormControlLabel
            label={settingName}
            control={
              <Switch
                checked={enableProjectShifts}
                onChange={(_, checked) => handleDefaultShifts(checked)}
                color="primary"
                disabled={!canEditShifts}
              />
            }
          />
        </RequestPermissionTooltip>
      </Grid>
      {enableProjectShifts && (
        <Grid size={12}>
          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
            Available Shifts:
          </Typography>
          {editMode && !canEditShifts && (
            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="textSecondary">
                {checkRequestExists(PermissionActionEnum.Approve, PermissionEntityEnum.CustomShifts)
                  ? 'You have already requested permission to approve custom shifts. Please await response from the administrator.'
                  : 'Permission to approve custom shifts is required to add or modify project shifts.'}
              </Typography>
              {!checkRequestExists(PermissionActionEnum.Approve, PermissionEntityEnum.CustomShifts) && (
                <Button
                  size="small"
                  variant="text"
                  sx={{ textTransform: 'none', fontSize: '0.75rem', p: 0, minWidth: 'auto' }}
                  onClick={() =>
                    handleRequestPermission(PermissionActionEnum.Approve, PermissionEntityEnum.CustomShifts)
                  }
                >
                  Request Access
                </Button>
              )}
            </Box>
          )}
          {objectLength(projectShifts) > 0 ? (
            projectShifts.map((shift, index) => (
              <Grid
                container
                key={index}
                sx={{ mb: 2 }}
                alignItems="baseline"
                justifyContent="space-between"
                spacing={1}
              >
                <Grid size={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Shift Name"
                    value={shift.name}
                    onChange={(e) => handleShiftChange(index, 'name', e.target.value)}
                    error={canEditShifts && shift.name.trim() === ''}
                    helperText={canEditShifts && shift.name.trim() === '' ? 'Required' : ''}
                    disabled={!canEditShifts}
                  />
                </Grid>

                <Grid size={3}>
                  <TimeAutocomplete
                    time={shift.startTime}
                    label="Start Time"
                    onTimeChange={(newValue) => handleTimeChange(index, 'startTime', newValue)}
                    disabled={!canEditShifts}
                  />
                </Grid>

                <Grid size={3}>
                  <TimeAutocomplete
                    time={shift.endTime}
                    label="End Time"
                    onTimeChange={(newValue) => handleTimeChange(index, 'endTime', newValue)}
                    disabled={!canEditShifts}
                  />
                </Grid>

                <Grid size="auto">
                  <RequestPermissionTooltip
                    hasPermission={canEditShifts}
                    requiredPermission={{
                      action: PermissionActionEnum.Approve,
                      entity: PermissionEntityEnum.CustomShifts,
                    }}
                    checkRequestExists={checkRequestExists}
                    onRequest={handleRequestPermission}
                    title="Delete Shift"
                  >
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteShift(index)}
                      size="small"
                      disabled={!canEditShifts}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </RequestPermissionTooltip>
                </Grid>
              </Grid>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary" textAlign="center">
              Add new shift
            </Typography>
          )}
        </Grid>
      )}
      {enableProjectShifts && (
        <Grid container justifyContent="flex-end" size={12}>
          <RequestPermissionTooltip
            hasPermission={canEditShifts}
            requiredPermission={{
              action: PermissionActionEnum.Approve,
              entity: PermissionEntityEnum.CustomShifts,
            }}
            checkRequestExists={checkRequestExists}
            onRequest={handleRequestPermission}
            title="Add Shift"
          >
            <Button
              color="primary"
              size="small"
              onClick={handleAddShift}
              variant="outlined"
              disabled={!canEditShifts || projectShifts.some((shift) => shift.name.trim() === '')}
            >
              <AddIcon />
            </Button>
          </RequestPermissionTooltip>
        </Grid>
      )}
    </Grid>
  );
};

export default ProjectShifts;
